import { useEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";
import type { T1InjectionType } from "../../../common-deprecated/hooks/useT1ModalInjection";
import type useT1ModalInjection from "../../../common-deprecated/hooks/useT1ModalInjection";
import { getPostal } from "../../../common-deprecated/utils/postalUtils";
import { setFinanceQuoteId, startFinanceFetch } from "../redux/actions/UscFinanceActions";
import type { FinanceCalculatorEventObject } from "../utils/financeUtils";
import { getFinanceInsurancePayloadSelector } from "../utils/financeUtils";
import { UscContext } from "../../shared-logic/types/UscCommonTypes";
import { setFinanceOption } from "../redux/actions/UscBasketActions";
import { hideModal, showModal, updateModalSettings } from "../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_USC_INJECTION } from "../../shared-logic/utils/modalConstants";
import { useUscDispatch, useUscSelector } from "../redux/store";
import type { UscTrackingLocation } from "../utils/tracking";
import { trackCta } from "../utils/tracking";
import {
    POSTAL_CHANNEL_T1_FINANCE,
    POSTAL_CHANNEL_FQS,
    POSTAL_TOPIC_FQS_QUOTE_SET,
    POSTAL_TOPIC_USC_FINANCE,
    POSTAL_TOPIC_USC_T1_RENDERED,
} from "../../../common-deprecated/constants";

type UseUscFinanceCalculatorButtonType = {
    isDisabled: boolean; // Should be added to disabled state of the button
    showCalculator?: () => void; // Open the finance calculator
};

const useUscFinanceCalculatorButton = (trackingLocation: UscTrackingLocation): UseUscFinanceCalculatorButtonType => {
    // ----------------------------------------------------------------------
    // Redux data
    // ----------------------------------------------------------------------
    const dispatch = useUscDispatch();
    const financeModuleUrl = useUscSelector((state) => state.uscSettings.financeModuleUrl);
    const urlWebsiteInjection = useUscSelector((state) => state.commonSettings.urlWebsiteInjection);
    const financeConfig = useUscSelector(getFinanceInsurancePayloadSelector("finance"), shallowEqual);
    const vehicleType = useUscSelector((state) => state.vehicle.type);
    const country = useUscSelector((state) => state.commonSettings.country);
    const [isDisabled, setIsDisabled] = useState(false);

    // ----------------------------------------------------------------------
    // Injection logic
    // ----------------------------------------------------------------------
    const fqsPostalSubscription = useRef<ReturnType<Postal["subscribe"]>>();
    const t1InjectionComponents = useRef<T1InjectionType>();
    const showAfterInit = useRef<boolean>(false);

    const [ready, setReady] = useState<boolean>(false);

    const injectionProps: Parameters<typeof useT1ModalInjection>[0] = {
        urlWebsite: urlWebsiteInjection,
        containerId: "cc-injection-finance-amd",
        contentUrl: financeModuleUrl,
    };

    // Load T1 injection logic, listen to financeQuoteId updates.
    useEffect(() => {
        window.require([`${urlWebsiteInjection}/scripts/T1-injection-components.js`], (components) => {
            t1InjectionComponents.current = components;
        });

        setReady(true);
        if (showAfterInit.current) openModal();
    }, []);

    // Listen for Makolab to send us a rendered event,
    // indicating that "behind the scenes" the finance calculator has been fully loaded.
    // For us that means we should hide the loading spinner, and show the calculator.
    useEffect(() => {
        const channel = vehicleType === UscContext.Used ? POSTAL_CHANNEL_T1_FINANCE : POSTAL_CHANNEL_FQS;
        const topic = POSTAL_TOPIC_USC_T1_RENDERED;

        const subscription = getPostal().subscribe({
            channel,
            topic,
            callback: () => {
                dispatch(updateModalSettings({ injectionLoading: false }, MODAL_USC_INJECTION));
            },
        });

        return () => {
            subscription.unsubscribe();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleType]);

    const onClose = (): void => {
        dispatch(hideModal(MODAL_USC_INJECTION));
        if (fqsPostalSubscription.current) getPostal().unsubscribe(fqsPostalSubscription.current);
        // This event is needed. Cleans up code on makolab side. If this is not present the finance calculator will crash when opening additional times
        document.dispatchEvent(new CustomEvent("bss-destroy-connector-event"));

        // The calculator button is disabled during the closing animation to make sure the modal can be properly cleared
        setIsDisabled(true);
    };

    const onCloseAnimationEnd = (): void => {
        // The calculator button is re-enabled once the closing animation is finished.
        setIsDisabled(false);
        // The finance modal of Makolab doesn't work properly when it's injected twice in the same container.
        // Therefor we clear the finance modal completely from the DOM after the animation is finished.
        // We disable the finance button during this timeframe to make sure the modal is not removed during reopening
        dispatch(hideModal(MODAL_USC_INJECTION));
    };

    // non-TFS countries use a different way of injecting the finance calculator
    // Currently this is only relevant for the TBA countries - if this ever changes,
    // please refactor this to a more dynamic solution.
    const useNonTFSInjection = ["ee", "lt", "lv"].includes(country);

    const openModal = (): void => {
        if (!ready) {
            // Sometimes it can take a while for the T1 logic to be properly initiated.
            // Setting showAfterInit will cause this function to be called again after initiation.
            showAfterInit.current = true;
            return;
        }

        dispatch(
            showModal({
                type: MODAL_USC_INJECTION,
                containerId: injectionProps.containerId,
                title: "",
                onClose,
                onCloseAnimationEnd,
                injectionLoading: true,
                // the tor-* classNames are specific for the Finance Calculator injection
                // and should not be reused anywhere else, or-* classnames are for billboard/salesman
                className: "tor-modal-content or-modal-content",
            }),
        );

        // Wait for the modal to fully initialize
        window.requestAnimationFrame(() => {
            // To be able to catch errors in the injection, we can't use the inject() function from T1.
            // Instead, we replicate what it does, but with a callback for error handling.
            // See: https://github.com/Toyota-Europe/dxp-aem/blob/99893c0e18f5692a5f272525f9622d531937ff68/ui.frontend/src/main/webpack/brands/common/base/js/dxp-amdInjections.old
            window.require(
                injectionProps.contentUrl,
                (module) => {
                    const renderObject = {
                        target: useNonTFSInjection
                            ? { 0: document.querySelector(`#${injectionProps.containerId}`) }
                            : document.querySelector(`#${injectionProps.containerId}`),
                        configuration: financeConfig,
                        isAppendedToBody: false,
                        module,
                        ...(useNonTFSInjection ? { url: injectionProps.contentUrl } : {}),
                    };

                    module.render(renderObject);
                },
                // Error callback: doesn't return anything, though is triggered when something goes wrong.
                (_) => {
                    dispatch(
                        updateModalSettings({ injectionLoading: false, injectionError: true }, MODAL_USC_INJECTION),
                    );
                },
            );
        });
    };

    const showCalculator = (): void => {
        dispatch(setFinanceOption("monthly"));

        trackCta("finance-calculator", trackingLocation, vehicleType);
        openModal();

        // There's a difference between channel/topic for used and stock cars.
        const channel = vehicleType === UscContext.Used ? POSTAL_CHANNEL_T1_FINANCE : POSTAL_CHANNEL_FQS;
        const topic = vehicleType === UscContext.Used ? POSTAL_TOPIC_USC_FINANCE : POSTAL_TOPIC_FQS_QUOTE_SET;

        fqsPostalSubscription.current = getPostal().subscribe({
            channel,
            topic,
            callback: (data: FinanceCalculatorEventObject) => {
                // Even though there is data available in the data object it is very inaccurate and not up to date.
                // Instead, only get the financeQuoteId and start a fetch to the API.
                dispatch(setFinanceQuoteId(data.financeQuoteId));
                dispatch(startFinanceFetch());
                onClose();
            },
        });
    };

    return {
        isDisabled,
        showCalculator: financeModuleUrl ? () => showCalculator() : undefined,
    };
};
export default useUscFinanceCalculatorButton;
